import React, { useEffect, useRef, useState } from 'react';
import { IconName } from '../../assets/icons';
import DragIndicator from './DragIndicator';

export interface WindowProps {
    closeWindow: () => void;
    minimizeWindow: () => void;
    onInteract: () => void;
    width: number;
    height: number;
    top: number;
    left: number;
    windowTitle?: string;
    bottomLeftText?: string;
    rainbow?: boolean;
    windowBarColor?: string;
    windowBarIcon?: IconName;
    onWidthChange?: (width: number) => void;
    onHeightChange?: (height: number) => void;
}

const Window: React.FC<WindowProps> = (props) => {
    const windowRef = useRef<any>(null);
    const dragRef = useRef<any>(null);
    const contentRef = useRef<any>(null);

    const dragProps = useRef<{
        dragStartX: any;
        dragStartY: any;
    }>();

    const [top, setTop] = useState(props.top);
    const [hoverClose, setHoverClose] = useState(false);
    const [hoverMinimize, setHoverMinimize] = useState(false);
    const [hoverMaximize, setHoverMaximize] = useState(false);
    const [left, setLeft] = useState(props.left);

    const lastClickInside = useRef(false);

    const [width, setWidth] = useState(props.width);
    const [height, setHeight] = useState(props.height);

    const [contentWidth, setContentWidth] = useState(props.width);
    const [contentHeight, setContentHeight] = useState(props.height);

    const [windowActive, setWindowActive] = useState(true);

    const [isMaximized, setIsMaximized] = useState(false);
    const [preMaxSize, setPreMaxSize] = useState({
        width,
        height,
        top,
        left,
    });

    const [isDragging, setIsDragging] = useState(false);

    const startDrag = (event: any) => {
        const { clientX, clientY } = event;
        setIsDragging(true);
        event.preventDefault();
        dragProps.current = {
            dragStartX: clientX,
            dragStartY: clientY,
        };
        window.addEventListener('mousemove', onDrag, false);
        window.addEventListener('mouseup', stopDrag, false);
    };

    const onDrag = ({ clientX, clientY }: any) => {
        let { x, y } = getXYFromDragProps(clientX, clientY);
        dragRef.current.style.transform = `translate(${x}px, ${y}px)`;
        dragRef.current.style.opacity = 1;
    };

    const stopDrag = ({ clientX, clientY }: any) => {
        setIsDragging(false);
        // dragRef.current.style.opacity = 0;
        const { x, y } = getXYFromDragProps(clientX, clientY);
        setTop(y);
        setLeft(x);
        window.removeEventListener('mousemove', onDrag, false);
        window.removeEventListener('mouseup', stopDrag, false);
    };

    const getXYFromDragProps = (
        clientX: number,
        clientY: number
    ): { x: number; y: number } => {
        if (!dragProps.current) return { x: 0, y: 0 };
        const { dragStartX, dragStartY } = dragProps.current;

        const x = clientX - dragStartX + left;
        const y = clientY - dragStartY + top;

        return { x, y };
    };

    useEffect(() => {
        dragRef.current.style.transform = `translate(${left}px, ${top}px)`;
    });

    useEffect(() => {
        props.onWidthChange && props.onWidthChange(contentWidth);
    }, [props.onWidthChange, contentWidth]); // eslint-disable-line

    useEffect(() => {
        props.onHeightChange && props.onHeightChange(contentHeight);
    }, [props.onHeightChange, contentHeight]); // eslint-disable-line

    useEffect(() => {
        setContentWidth(contentRef.current.getBoundingClientRect().width);
    }, [width]);

    useEffect(() => {
        setContentHeight(contentRef.current.getBoundingClientRect().height);
    }, [height]);

    const ENLARGE_STEP_W = 80;
    const ENLARGE_STEP_H = 60;

    const maximize = () => {
        if (isMaximized) {
            setWidth(preMaxSize.width);
            setHeight(preMaxSize.height);
            setTop(preMaxSize.top);
            setLeft(preMaxSize.left);
            setIsMaximized(false);
        } else {
            setPreMaxSize({
                width,
                height,
                top,
                left,
            });
            const newW = Math.min(width + ENLARGE_STEP_W, window.innerWidth - 40);
            const newH = Math.min(height + ENLARGE_STEP_H, window.innerHeight - 60);
            setWidth(newW);
            setHeight(newH);
            const maxLeft = window.innerWidth - newW - 20;
            const maxTop = window.innerHeight - newH - 40;
            setLeft(Math.max(10, Math.min(left, maxLeft)));
            setTop(Math.max(10, Math.min(top, maxTop)));
            setIsMaximized(true);
        }
    };

    const onCheckClick = () => {
        if (lastClickInside.current) {
            setWindowActive(true);
        } else {
            setWindowActive(false);
        }
        lastClickInside.current = false;
    };

    useEffect(() => {
        window.addEventListener('mousedown', onCheckClick, false);
        return () => {
            window.removeEventListener('mousedown', onCheckClick, false);
        };
    }, []);

    const onWindowInteract = () => {
        props.onInteract();
        setWindowActive(true);
        lastClickInside.current = true;
    };

    return (
        <div onMouseDown={onWindowInteract} style={styles.container}>
            <div
                style={Object.assign({}, styles.window, {
                    width,
                    height,
                    top,
                    left,
                })}
                ref={windowRef}
            >
                <div style={styles.windowBorderOuter}>
                    <div style={styles.windowBorderInner}>
                        <div
                            style={styles.dragHitbox}
                            onMouseDown={startDrag}
                        ></div>
                        <div
                            className={props.rainbow ? 'rainbow-wrapper' : ''}
                            style={Object.assign(
                                {},
                                styles.topBar,
                                props.windowBarColor && {
                                    backgroundColor: props.windowBarColor,
                                },
                                !windowActive && {
                                    backgroundColor: 'rgba(30, 30, 30, 0.85)',
                                }
                            )}
                        >
                            <div style={styles.windowHeader}>
                                <p
                                    style={
                                        windowActive
                                            ? styles.titleText
                                            : { ...styles.titleText, color: 'rgba(255,255,255,0.5)' }
                                    }
                                    className="showcase-header"
                                >
                                    {props.windowTitle}
                                </p>
                            </div>
                            <div style={styles.windowTopButtons}>
                                <button
                                    type="button"
                                    style={Object.assign({}, styles.trafficLight, styles.trafficClose)}
                                    onMouseDown={(e) => { e.preventDefault(); props.closeWindow(); }}
                                    onMouseEnter={() => setHoverClose(true)}
                                    onMouseLeave={() => setHoverClose(false)}
                                    title="Close"
                                >
                                    {hoverClose && <span style={styles.trafficSymbol}>×</span>}
                                </button>
                                <button
                                    type="button"
                                    style={Object.assign({}, styles.trafficLight, styles.trafficMinimize)}
                                    onMouseDown={(e) => { e.preventDefault(); props.minimizeWindow(); }}
                                    onMouseEnter={() => setHoverMinimize(true)}
                                    onMouseLeave={() => setHoverMinimize(false)}
                                    title="Minimize"
                                >
                                    {hoverMinimize && <span style={styles.trafficSymbol}>−</span>}
                                </button>
                                <button
                                    type="button"
                                    style={Object.assign({}, styles.trafficLight, styles.trafficMaximize)}
                                    onMouseDown={(e) => { e.preventDefault(); maximize(); }}
                                    onMouseEnter={() => setHoverMaximize(true)}
                                    onMouseLeave={() => setHoverMaximize(false)}
                                    title="Maximize"
                                >
                                    {hoverMaximize && <span style={styles.trafficSymbol}>+</span>}
                                </button>
                            </div>
                        </div>
                        <div
                            style={styles.contentOuter}
                        >
                            <div style={styles.contentInner}>
                                <div style={styles.content} ref={contentRef}>
                                    {props.children}
                                </div>
                            </div>
                        </div>
                        {props.bottomLeftText ? (
                            <div style={styles.bottomBar}>
                                <p style={styles.bottomBarText}>{props.bottomLeftText}</p>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            <div
                style={
                    !isDragging
                        ? {
                              zIndex: -10000,
                              pointerEvents: 'none',
                          }
                        : {
                              zIndex: 1000,
                              cursor: 'move',
                              mixBlendMode: 'difference',
                          }
                }
            >
                <DragIndicator
                    width={width}
                    height={height}
                    dragRef={dragRef}
                />
            </div>
        </div>
    );
};

const styles: StyleSheetCSS = {
    window: {
        backgroundColor: 'transparent',
        position: 'absolute',
        borderRadius: 12,
        overflow: 'hidden',
    },
    dragHitbox: {
        position: 'absolute',
        width: 'calc(100% - 70px)',
        height: 40,
        zIndex: 10000,
        top: 0,
        left: 0,
        cursor: 'move',
    },
    windowBorderOuter: {
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 12,
        flex: 1,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)',
    },
    windowBorderInner: {
        flex: 1,
        padding: 0,
        flexDirection: 'column',
    },
    topBar: {
        backgroundColor: 'rgba(50, 50, 55, 0.65)',
        backdropFilter: 'blur(24px) saturate(150%)',
        WebkitBackdropFilter: 'blur(24px) saturate(150%)',
        width: '100%',
        height: 40,
        minHeight: 40,
        alignItems: 'center',
        paddingRight: 12,
        boxSizing: 'border-box',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
    },
    titleText: {
        color: 'rgba(255, 255, 255, 0.95)',
        fontSize: 13,
    },
    contentOuter: {
        borderTop: 'none',
        flexGrow: 1,
        marginTop: 0,
        marginBottom: 0,
        overflow: 'hidden',
    },
    contentInner: {
        flex: 1,
        overflow: 'hidden',
    },
    content: {
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'hidden',
        minHeight: 0,
        backgroundColor: '#1e1e1e',
        color: '#e8e8e8',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
    },
    bottomBar: {
        flexShrink: 0,
        width: '100%',
        height: 20,
        display: 'flex',
        alignItems: 'center',
        borderTop: '1px solid rgba(255, 255, 255, 0.06)',
        backgroundColor: 'rgba(30, 30, 30, 0.95)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
        paddingLeft: 10,
    },
    bottomBarText: {
        fontSize: 11,
        color: 'rgba(255, 255, 255, 0.6)',
        margin: 0,
    },
    windowTopButtons: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 8,
    },
    trafficLight: {
        width: 12,
        height: 12,
        borderRadius: 6,
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    trafficSymbol: {
        fontSize: 11,
        fontWeight: 600,
        color: 'rgba(0, 0, 0, 0.75)',
        lineHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'translateY(-1px)',
    },
    trafficClose: {
        backgroundColor: '#ff5f57',
        boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.1)',
    },
    trafficMinimize: {
        backgroundColor: '#febc2e',
        boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.1)',
    },
    trafficMaximize: {
        backgroundColor: '#28c840',
        boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.1)',
    },
    windowHeader: {
        flex: 1,
        paddingLeft: 12,
        minWidth: 0,
    },
    windowBarIcon: {
        paddingLeft: 12,
        paddingRight: 6,
    },
};

export default Window;
